import type { Root } from "remark-gfm";
import { visit } from "unist-util-visit";

export const remarkCustomExtensionsPlugin = () => {
  return (tree: Root) => {
    const sections: { name: string; start: number; end: number }[] = [];
    let currentSection: { name: string; start: number } | null = null;

    // First pass: Process HTML nodes for opening tags
    visit(tree, "html", (node, index, parent) => {
      if (!parent || index === null || index === undefined) return;

      const htmlValue = node.value as string;

      // Check for section opening tags
      const startMatch = htmlValue.match(/^<([^\/][^>]*)>$/);
      if (startMatch && !currentSection) {
        currentSection = {
          name: startMatch[1]?.trim() ?? "",
          start: index, // index is guaranteed to be a number here because of the check above
        };
        return;
      }

      // Check for section closing tags
      const endMatch = htmlValue.match(/^<\/([^>]*)>$/);
      if (endMatch && currentSection && endMatch[1]?.trim() === currentSection.name) {
        sections.push({
          name: currentSection.name,
          start: currentSection.start,
          end: index, // index is guaranteed to be a number here
        });
        currentSection = null;
      }
    });

    // Keep the existing text node processing for backward compatibility
    visit(tree, "text", (node, index, parent) => {
      if (!parent) return;

      const text = node.value as string;
      // Handle both old format [[[tag]]] and <tag> format
      const parts = text.split(/(<[^\/][^>]*>|<\/[^>]*>|\[\[\[.*?\]\]\]|\[\[\/.*?\]\]\])/g);

      if (parts.length > 1) {
        const newNodes = parts
          .filter(Boolean) // Remove empty strings
          .map((part) => ({
            type: "text" as const,
            value: part.trim(),
            position: node.position,
          }));

        const nodeIndex = parent.children.indexOf(node);
        parent.children.splice(nodeIndex, 1, ...newNodes);
      }
    });

    // Process paragraphs for both HTML and bracket formats
    visit(tree, "paragraph", (paragraphNode, paragraphIndex) => {
      if (!Array.isArray(paragraphNode.children) || paragraphIndex === null || paragraphIndex === undefined) return;

      paragraphNode.children.forEach((node) => {
        if (node.type !== "text") return;
        const text = node.value as string;

        // Match section start: <tagname>
        const startHtmlMatch = text.match(/^<([^\/][^>]*)>$/);
        if (startHtmlMatch && !currentSection) {
          currentSection = {
            name: startHtmlMatch[1]?.trim() ?? "",
            start: paragraphIndex,
          };
          return;
        }

        // Match section end: </tagname>
        const endHtmlMatch = text.match(/^<\/([^>]*)>$/);
        if (endHtmlMatch && currentSection && endHtmlMatch[1]?.trim() === currentSection.name) {
          sections.push({
            name: currentSection.name,
            start: currentSection.start,
            end: paragraphIndex,
          });
          currentSection = null;
          return;
        }

        // Match legacy section start: [[[Section Name]]]
        const startBracketMatch = text.match(/^\[\[\[(.*?)\]\]\]$/);
        if (startBracketMatch && !currentSection) {
          currentSection = {
            name: startBracketMatch[1]?.trim() ?? "",
            start: paragraphIndex,
          };
          return;
        }

        // Match legacy section end: [[[/Section Name]]]
        const endBracketMatch = text.match(/^\[\[\[\/(.*?)\]\]\]$/);
        if (endBracketMatch && currentSection && endBracketMatch[1]?.trim() === currentSection.name) {
          sections.push({
            name: currentSection.name,
            start: currentSection.start,
            end: paragraphIndex,
          });
          currentSection = null;
        }
      });
    });

    // Third pass: transform sections into custom nodes
    for (const section of sections.reverse()) {
      if (!tree.children) continue;

      // Get all nodes between start and end, including the start/end nodes
      const sectionContent = tree.children.slice(section.start, section.end + 1);

      // Remove the opening and closing tag nodes (both formats)
      const filteredContent = sectionContent.filter((node, idx) => {
        // Skip the first and last nodes if they contain opening/closing tags
        if (idx === 0) {
          if (node.type === "html") {
            const htmlValue = node.value as string;
            return !htmlValue.match(/^<[^\/][^>]*>$/);
          }
          if (node.type === "paragraph" && Array.isArray(node.children)) {
            // Check if it contains a bracket tag
            const containsBracketTag = node.children.some(
              (child) => child.type === "text" && /^\[\[\[.*?\]\]\]$/.test(child.value as string),
            );

            if (containsBracketTag) {
              // Remove the tag but keep other content in the paragraph
              node.children = node.children.filter(
                (child) => child.type !== "text" || !/^\[\[\[.*?\]\]\]$/.test(child.value as string),
              );
              // If paragraph is now empty, skip it
              return node.children.length > 0;
            }
          }
        }

        if (idx === sectionContent.length - 1) {
          if (node.type === "html") {
            const htmlValue = node.value as string;
            return !htmlValue.match(/^<\/[^>]*>$/);
          }
          if (node.type === "paragraph" && Array.isArray(node.children)) {
            // Check if it contains a bracket closing tag
            const containsBracketClosingTag = node.children.some(
              (child) => child.type === "text" && /^\[\[\[\/.*?\]\]\]$/.test(child.value as string),
            );

            if (containsBracketClosingTag) {
              // Remove the tag but keep other content in the paragraph
              node.children = node.children.filter(
                (child) => child.type !== "text" || !/^\[\[\[\/.*?\]\]\]$/.test(child.value as string),
              );
              // If paragraph is now empty, skip it
              return node.children.length > 0;
            }
          }
        }
        return true;
      });

      const sectionNode = {
        type: "section",
        name: section.name,
        children: filteredContent as typeof tree.children,
        data: {
          hName: "div",
          hProperties: {
            className: ["custom-section"],
            "data-section-name": section.name,
          },
        },
      } as unknown as (typeof tree.children)[number];

      // Replace the section content with our custom node
      tree.children.splice(section.start, section.end - section.start + 1, sectionNode);
    }

    // Now wrap any content that's not in a section with an implicit <answer> section
    if (tree.children) {
      // Find all nodes that aren't already in a custom section
      const nonSectionNodes: number[][] = [];
      let currentNonSectionGroup: number[] = [];

      tree.children.forEach((node, index) => {
        // Check for our custom section nodes using the data property that's unique to them
        const isCustomSection =
          typeof node === "object" &&
          node !== null &&
          "data" in node &&
          node.data &&
          typeof node.data === "object" &&
          "hProperties" in node.data &&
          node.data.hProperties &&
          typeof node.data.hProperties === "object" &&
          "data-section-name" in node.data.hProperties;

        if (isCustomSection) {
          // If we have accumulated non-section nodes, save that group
          if (currentNonSectionGroup.length > 0) {
            nonSectionNodes.push([...currentNonSectionGroup]);
            currentNonSectionGroup = [];
          }
        } else {
          currentNonSectionGroup.push(index);
        }
      });

      // Add any remaining non-section nodes
      if (currentNonSectionGroup.length > 0) {
        nonSectionNodes.push(currentNonSectionGroup);
      }

      // Wrap each group of non-section nodes in an answer section
      // Process in reverse to avoid index shifting issues
      for (const indices of nonSectionNodes.reverse()) {
        if (indices.length === 0) continue;

        const startIdx = indices[0];
        const endIdx = indices[indices.length - 1];

        if (startIdx === undefined || endIdx === undefined) continue;

        // Extract the nodes
        const contentNodes = tree.children.slice(startIdx, endIdx + 1);

        // Create an answer section node
        const answerSection = {
          type: "section",
          name: "answer",
          children: contentNodes,
          data: {
            hName: "div",
            hProperties: {
              className: ["custom-section"],
              "data-section-name": "answer",
            },
          },
        } as unknown as (typeof tree.children)[number];

        // Replace the original nodes with the answer section
        tree.children.splice(startIdx, indices.length, answerSection);
      }
    }

    // Debug logging
    // console.log("Sections found:", sections);
  };
};

export function preprocessMarkdown(markdown: string): string {
  // Handle both old [[[tag]]] format and <tag> format
  return markdown.replace(/\n*(<[^\/][^>]*>|<\/[^>]*>|\[\[\[.*?\]\]\]|\[\[\/.*?\]\]\])\n*/g, "\n\n$1\n\n");
}
